import type {
  InboundCampaignComposeRequest,
  InboundComposeFormValues,
  InboundGroupRow,
} from './types';

/**
 * Build the wire-format body the legacy `/api/obd/inbound/campaign/compose`
 * endpoint expects. Some quirks preserved verbatim:
 *
 *   - `templateId` is always "6" (Inbound Call Patch).
 *   - `groupRows` is double-wrapped:
 *       JSON.stringify({ groupRows: [{groupId, groupName, agentDtmf}] })
 *   - `clis` is also wrapped: JSON.stringify({ cliList: [...] }); empty
 *     string when nothing is picked.
 *   - `location` is hardcoded to "indore" in legacy — preserved.
 *   - Optional voice file PIds zero out when the matching toggle is off.
 */
export function buildInboundComposeRequest(args: {
  userId: number;
  values: InboundComposeFormValues;
}): InboundCampaignComposeRequest {
  const { userId, values } = args;

  return {
    userId,
    campaignName: values.campaignName.trim(),
    templateId: '6',
    dtmf: values.dtmf.join(','),
    welcomePId: values.useWelcomeFile ? values.welcomeId : '',
    menuPId: values.menuId,
    noInputPId: values.useNoinputFile ? values.noinputId : '',
    wrongInputPId: values.useWronginputFile ? values.wronginputId : '',
    thanksPId: values.useThanksFile ? values.thanksId : '',
    noAgentId: values.noAgentId,
    smsSuccessApi: values.sendSmsSuccess ? values.smsApiSuccess : '',
    smsSuccessMessage: '',
    smsFailApi: values.sendSmsFail ? values.smsApiFail : '',
    smsFailMessage: '',
    smsDtmfApi: values.sendSmsDtmf ? values.smsApiDtmf : '',
    smsDtmfMessage: '',
    callDurationSMS: values.sendSmsSuccess ? values.callDurationSMS : 0,
    retries: Number(values.retries) || 0,
    retryInterval: Number(values.retryInterval) || 0,
    groupRows: JSON.stringify({ groupRows: values.groupRows }),
    menuWaitTime: values.menuWaitTime,
    rePrompt: values.rePrompt,
    /** Legacy ivrId is unused for inbound; keep blank for back-compat. */
    ivrId: '',
    clis:
      values.selectedCLI.length === 0
        ? ''
        : JSON.stringify({ cliList: values.selectedCLI }),
    location: 'indore',
  };
}

/**
 * Best-effort hydrator for the `/api/obd/inbound/campaign/clone/{id}` payload.
 * The endpoint returns a free-form object — read each field tolerantly and
 * fall back to defaults so the form still renders on partial data.
 */
export function hydrateInboundFromClone(
  raw: unknown,
): Partial<InboundComposeFormValues> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;

  const str = (v: unknown) =>
    v === null || v === undefined ? '' : String(v);
  const num = (v: unknown, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const json = <T,>(v: unknown, fallback: T): T => {
    if (typeof v !== 'string' || !v.trim()) return fallback;
    try {
      return JSON.parse(v) as T;
    } catch {
      return fallback;
    }
  };

  const groupWrap = json<{ groupRows?: InboundGroupRow[] }>(r.groupRows, {
    groupRows: [],
  });
  const cliWrap = json<{ cliList?: Array<string | number> }>(r.clis, {
    cliList: [],
  });

  const dtmf = (() => {
    const v = r.dtmf;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.trim()) {
      return v.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  })();

  const welcomeId = str(r.welcomePId);
  const noinputId = str(r.noInputPId);
  const wronginputId = str(r.wrongInputPId);
  const thanksId = str(r.thanksPId);

  return {
    campaignName: str(r.campaignName),
    useWelcomeFile: Boolean(welcomeId),
    useNoinputFile: Boolean(noinputId),
    useWronginputFile: Boolean(wronginputId),
    useThanksFile: Boolean(thanksId),
    welcomeId,
    menuId: str(r.menuPId),
    noinputId,
    wronginputId,
    thanksId,
    noAgentId: str(r.noAgentId),
    dtmf,
    menuWaitTime: str(r.menuWaitTime),
    rePrompt: str(r.rePrompt),
    groupRows: (groupWrap.groupRows ?? []).map((g) => ({
      groupId: str(g.groupId),
      groupName: str(g.groupName),
      agentDtmf: str(g.agentDtmf),
    })),
    retries: num(r.retries),
    retryInterval: num(r.retryInterval),
    selectedCLI: (cliWrap.cliList ?? []).map(String),
    sendSmsSuccess: Boolean(r.smsSuccessApi),
    smsApiSuccess: str(r.smsSuccessApi),
    callDurationSMS: num(r.callDurationSMS),
    sendSmsFail: Boolean(r.smsFailApi),
    smsApiFail: str(r.smsFailApi),
    sendSmsDtmf: Boolean(r.smsDtmfApi),
    smsApiDtmf: str(r.smsDtmfApi),
  };
}
